import React, { useState, useEffect } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Settings as SettingsIcon, Plus, Trash2, ArrowUpRight, ArrowDownRight, UserPlus, Users, Bell, BellRing, Loader2, Mail, Crown, Coins, Search, Star, DoorOpen, Eye, Check, X, Inbox, Landmark } from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { pushSupported, getPushStatus, enablePush, testPush } from "@/lib/push";

const ROLE_LABEL = { owner: "Dueño", member: "Colaborador", viewer: "Solo lectura" };

function MoneyTab() {
  const { checkAuth } = useAuth();
  const qc = useQueryClient();
  const { data: catalog = [] } = useQuery({ queryKey: ["currency-catalog"], queryFn: async () => (await api.get("/catalog/currencies")).data });
  const { data: settings } = useQuery({ queryKey: ["settings"], queryFn: async () => (await api.get("/settings")).data });
  const [sel, setSel] = useState(null);
  const [def, setDef] = useState("MXN");
  const [q, setQ] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => { if (settings) { setSel(settings.currencies); setDef(settings.default_currency); } }, [settings]);
  if (!sel) return <div className="py-8 text-center"><Loader2 className="w-5 h-5 animate-spin mx-auto" /></div>;

  const toggle = (code) => {
    if (code === "MXN") return;
    setSel((s) => s.includes(code) ? s.filter((x) => x !== code) : [...s, code]);
  };
  const filtered = catalog.filter((c) => c.code.toLowerCase().includes(q.toLowerCase()) || c.name.toLowerCase().includes(q.toLowerCase()));
  const save = async () => {
    setSaving(true);
    try {
      const default_currency = sel.includes(def) ? def : "MXN";
      await api.put("/settings", { default_currency, currencies: sel });
      toast.success("Monedas actualizadas");
      await checkAuth();
      qc.invalidateQueries();
    } catch { toast.error("No se pudo guardar"); } finally { setSaving(false); }
  };

  return (
    <div className="rounded-2xl border border-border bg-card p-5 max-w-2xl" data-testid="money-tab">
      <div className="flex items-center gap-2 mb-1"><Coins className="w-5 h-5 text-emerald-500" /><h3 className="font-semibold">Monedas de la aplicación</h3></div>
      <p className="text-xs text-muted-foreground mb-4">Elige qué monedas aparecen en el selector de toda la app y cuál es la predeterminada. MXN es la base y siempre está activa.</p>
      <div className="flex flex-col sm:flex-row gap-3 mb-4">
        <div className="relative flex-1">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input data-testid="currency-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Buscar moneda…" className="pl-9 rounded-xl" />
        </div>
        <Button data-testid="save-currencies-button" onClick={save} disabled={saving} className="rounded-xl gap-2">
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : null} Guardar ({sel.length})
        </Button>
      </div>
      <div className="grid sm:grid-cols-2 gap-1.5 max-h-[420px] overflow-y-auto pr-1">
        {filtered.map((c) => {
          const on = sel.includes(c.code);
          const isDefault = def === c.code;
          return (
            <div key={c.code} data-testid={`currency-item-${c.code}`}
              className={`flex items-center gap-3 rounded-xl border px-3 py-2.5 ${on ? "border-emerald-500/40 bg-emerald-500/5" : "border-border"}`}>
              <Checkbox checked={on} disabled={c.code === "MXN"} onCheckedChange={() => toggle(c.code)} data-testid={`currency-check-${c.code}`} />
              <div className="min-w-0 flex-1">
                <p className="text-sm font-medium font-numeric">{c.code}</p>
                <p className="text-xs text-muted-foreground truncate">{c.name}</p>
              </div>
              <button title="Predeterminada" data-testid={`currency-default-${c.code}`} disabled={!on}
                onClick={() => setDef(c.code)}
                className={`w-8 h-8 rounded-lg flex items-center justify-center ${isDefault ? "text-amber-500" : "text-muted-foreground hover:text-amber-500"} ${!on ? "opacity-30 cursor-not-allowed" : ""}`}>
                <Star className="w-4 h-4" fill={isDefault ? "currentColor" : "none"} />
              </button>
            </div>
          );
        })}
      </div>
      <p className="text-xs text-muted-foreground mt-3">Predeterminada actual: <span className="font-numeric font-semibold text-foreground">{def}</span></p>
    </div>
  );
}

function CategoryColumn({ type, items, onAdd, onDelete }) {
  const [val, setVal] = useState("");
  const isBy = type === "by";
  return (
    <div className="rounded-2xl border border-border bg-card p-5">
      <div className="flex items-center gap-2 mb-4">
        {isBy ? <ArrowUpRight className="w-5 h-5 text-emerald-500" /> : <ArrowDownRight className="w-5 h-5 text-rose-500" />}
        <h3 className={`font-semibold ${isBy ? "text-emerald-500" : "text-rose-500"}`}>{isBy ? "By · Ingresos" : "To · Gastos"}</h3>
      </div>
      <div className="flex gap-2 mb-4">
        <Input data-testid={`new-category-${type}`} value={val} onChange={(e) => setVal(e.target.value)} placeholder="Nueva categoría" className="rounded-xl"
          onKeyDown={(e) => { if (e.key === "Enter" && val.trim()) { onAdd(type, val.trim()); setVal(""); } }} />
        <Button size="icon" className="rounded-xl shrink-0" onClick={() => { if (val.trim()) { onAdd(type, val.trim()); setVal(""); } }}><Plus className="w-4 h-4" /></Button>
      </div>
      <div className="space-y-1.5">
        {items.map((c) => (
          <div key={c.id} className="flex items-center justify-between rounded-lg border border-border px-3 py-2">
            <span className="text-sm">{c.name}</span>
            <button onClick={() => onDelete(c.id)} className="text-muted-foreground hover:text-rose-500"><Trash2 className="w-4 h-4" /></button>
          </div>
        ))}
        {items.length === 0 && <p className="text-xs text-muted-foreground text-center py-3">Sin categorías</p>}
      </div>
    </div>
  );
}

function BanksTab() {
  const qc = useQueryClient();
  const { user } = useAuth();
  const canWrite = user?.role !== "viewer";
  const { data: settings } = useQuery({ queryKey: ["settings"], queryFn: async () => (await api.get("/settings")).data });
  const { data: banks = [] } = useQuery({ queryKey: ["banks"], queryFn: async () => (await api.get("/catalog/banks")).data });
  const [name, setName] = useState("");
  const [color, setColor] = useState("#10b981");
  const cur = settings?.default_currency || "MXN";
  const add = async () => {
    if (!name.trim()) return;
    try { await api.post("/banks", { name: name.trim(), color }); setName(""); qc.invalidateQueries({ queryKey: ["banks"] }); toast.success("Banco agregado"); }
    catch (e) { toast.error(e?.response?.data?.detail || "No se pudo agregar"); }
  };
  const del = async (id) => { try { await api.delete(`/banks/${id}`); qc.invalidateQueries({ queryKey: ["banks"] }); } catch (e) { toast.error(e?.response?.data?.detail || "Error"); } };
  return (
    <div className="rounded-2xl border border-border bg-card p-5 max-w-2xl" data-testid="banks-tab">
      <div className="flex items-center gap-2 mb-1"><Landmark className="w-5 h-5 text-emerald-500" /><h3 className="font-semibold">Bancos</h3></div>
      <p className="text-xs text-muted-foreground mb-4">Se muestran los bancos de <span className="font-numeric font-semibold text-foreground" data-testid="banks-currency">{cur}</span> según la moneda predeterminada de la bóveda (cámbiala en la pestaña Moneda). Estos bancos están disponibles al crear una cuenta.</p>
      {canWrite && (
        <div className="flex gap-2 mb-4">
          <input type="color" value={color} onChange={(e) => setColor(e.target.value)} data-testid="bank-color-input" className="w-11 h-11 rounded-xl border border-border bg-transparent p-1 cursor-pointer shrink-0" title="Color del banco" />
          <Input data-testid="new-bank-input" value={name} onChange={(e) => setName(e.target.value)} placeholder={`Agregar banco de ${cur}…`} className="rounded-xl" onKeyDown={(e) => e.key === "Enter" && add()} />
          <Button data-testid="add-bank-button" size="icon" className="rounded-xl shrink-0" onClick={add}><Plus className="w-4 h-4" /></Button>
        </div>
      )}
      <div className="grid sm:grid-cols-2 gap-1.5 max-h-[440px] overflow-y-auto pr-1">
        {banks.map((b) => (
          <div key={b.id} data-testid={`bank-item-${b.id}`} className="flex items-center gap-3 rounded-xl border border-border px-3 py-2.5">
            <span className="w-3 h-3 rounded-full shrink-0" style={{ background: b.color || "#64748b" }} />
            <span className="text-sm flex-1 truncate">{b.name}</span>
            {b.custom
              ? (canWrite && <button data-testid={`delete-bank-${b.id}`} onClick={() => del(b.id)} className="text-muted-foreground hover:text-rose-500"><Trash2 className="w-4 h-4" /></button>)
              : (b.id !== "efectivo" && <span className="text-[10px] uppercase tracking-wider text-muted-foreground">catálogo</span>)}
          </div>
        ))}
      </div>
    </div>
  );
}

export default function Settings() {
  const qc = useQueryClient();
  const { user, setUser } = useAuth();
  const isOwner = user?.role === "owner";
  const { data: categories = { by: [], to: [] } } = useQuery({ queryKey: ["categories"], queryFn: async () => (await api.get("/categories")).data });
  const { data: responsibles = [] } = useQuery({ queryKey: ["responsibles"], queryFn: async () => (await api.get("/responsibles")).data });
  const { data: members = [] } = useQuery({ queryKey: ["members"], queryFn: async () => (await api.get("/vaults/members")).data });
  const { data: invitations = [] } = useQuery({ queryKey: ["invitations"], queryFn: async () => (await api.get("/vaults/invitations")).data });

  const [resp, setResp] = useState("");
  const [invite, setInvite] = useState("");
  const [inviteRole, setInviteRole] = useState("member");
  const [pushStatus, setPushStatus] = useState("off");
  const [pushBusy, setPushBusy] = useState(false);

  useEffect(() => { getPushStatus().then(setPushStatus); }, []);

  const addCat = async (type, name) => { await api.post("/categories", { type, name }); qc.invalidateQueries({ queryKey: ["categories"] }); };
  const delCat = async (id) => { await api.delete(`/categories/${id}`); qc.invalidateQueries({ queryKey: ["categories"] }); };
  const addResp = async () => { if (!resp.trim()) return; await api.post("/responsibles", { name: resp.trim() }); setResp(""); qc.invalidateQueries({ queryKey: ["responsibles"] }); };
  const delResp = async (id) => { await api.delete(`/responsibles/${id}`); qc.invalidateQueries({ queryKey: ["responsibles"] }); };

  const sendInvite = async () => {
    if (!invite.trim()) return;
    try { await api.post("/vaults/invite", { email: invite.trim(), role: inviteRole }); toast.success("Invitación enviada · debe aceptarla"); setInvite(""); qc.invalidateQueries({ queryKey: ["members"] }); }
    catch (e) { toast.error(e?.response?.data?.detail || "No se pudo invitar"); }
  };
  const removeMember = async (id) => { try { await api.delete(`/vaults/members/${id}`); qc.invalidateQueries({ queryKey: ["members"] }); } catch (e) { toast.error(e?.response?.data?.detail || "Error"); } };
  const changeRole = async (id, role) => { try { await api.post(`/vaults/members/${id}/role`, { role }); qc.invalidateQueries({ queryKey: ["members"] }); toast.success("Rol actualizado"); } catch (e) { toast.error(e?.response?.data?.detail || "Error"); } };

  const acceptInvite = async (id) => {
    try { const r = await api.post(`/vaults/invitations/${id}/accept`); setUser(r.data); qc.invalidateQueries(); toast.success("Te uniste a la bóveda"); }
    catch (e) { toast.error(e?.response?.data?.detail || "Error"); }
  };
  const declineInvite = async (id) => {
    try { await api.post(`/vaults/invitations/${id}/decline`); qc.invalidateQueries({ queryKey: ["invitations"] }); toast.success("Invitación rechazada"); }
    catch (e) { toast.error(e?.response?.data?.detail || "Error"); }
  };

  const leaveVault = async () => {
    if (!window.confirm(`¿Seguro que quieres abandonar "${user?.vault_name}"? Perderás el acceso a sus movimientos.`)) return;
    try {
      const r = await api.post("/vaults/leave");
      setUser(r.data);
      qc.invalidateQueries();
      toast.success("Abandonaste la bóveda");
    } catch (e) { toast.error(e?.response?.data?.detail || "No se pudo abandonar"); }
  };

  const doEnablePush = async () => {
    setPushBusy(true);
    try { await enablePush(); setPushStatus("on"); toast.success("Notificaciones activadas"); }
    catch (e) { toast.error(e.message === "denied" ? "Permiso denegado en el navegador" : "No se pudo activar"); }
    finally { setPushBusy(false); }
  };
  const doTest = async () => { try { const r = await testPush(); toast.success(`Enviadas ${r.sent}/${r.subscriptions}`); } catch { toast.error("Error al enviar"); } };

  return (
    <div className="py-4 max-w-4xl" data-testid="settings-page">
      <div className="flex items-center gap-2"><SettingsIcon className="w-6 h-6 text-emerald-500" /><h1 className="text-3xl sm:text-4xl font-bold tracking-tight">Configuración</h1></div>
      <p className="text-muted-foreground text-sm mt-1 mb-8">Catálogos de cuentas, conceptos, miembros y recordatorios.</p>

      <Tabs defaultValue="categorias">
        <TabsList className="rounded-xl flex-wrap h-auto">
          <TabsTrigger value="categorias" data-testid="tab-categorias" className="rounded-lg">Conceptos</TabsTrigger>
          <TabsTrigger value="bancos" data-testid="tab-bancos" className="rounded-lg">Bancos</TabsTrigger>
          <TabsTrigger value="responsables" data-testid="tab-responsables" className="rounded-lg">Responsables</TabsTrigger>
          <TabsTrigger value="miembros" data-testid="tab-miembros" className="rounded-lg">Miembros</TabsTrigger>
          <TabsTrigger value="moneda" data-testid="tab-moneda" className="rounded-lg">Moneda</TabsTrigger>
          <TabsTrigger value="notificaciones" data-testid="tab-notificaciones" className="rounded-lg">Recordatorios</TabsTrigger>
        </TabsList>

        <TabsContent value="categorias" className="mt-6">
          <div className="grid md:grid-cols-2 gap-4">
            <CategoryColumn type="by" items={categories.by} onAdd={addCat} onDelete={delCat} />
            <CategoryColumn type="to" items={categories.to} onAdd={addCat} onDelete={delCat} />
          </div>
        </TabsContent>

        <TabsContent value="bancos" className="mt-6">
          <BanksTab />
        </TabsContent>

        <TabsContent value="responsables" className="mt-6">
          <div className="rounded-2xl border border-border bg-card p-5 max-w-lg">
            <div className="flex gap-2 mb-4">
              <Input data-testid="new-responsible" value={resp} onChange={(e) => setResp(e.target.value)} placeholder="Nombre (Personal, Pareja, Negocio…)" className="rounded-xl" onKeyDown={(e) => e.key === "Enter" && addResp()} />
              <Button size="icon" className="rounded-xl shrink-0" onClick={addResp}><Plus className="w-4 h-4" /></Button>
            </div>
            <div className="space-y-1.5">
              {responsibles.map((r) => (
                <div key={r.id} className="flex items-center justify-between rounded-lg border border-border px-3 py-2">
                  <span className="text-sm">{r.name}</span>
                  <button onClick={() => delResp(r.id)} className="text-muted-foreground hover:text-rose-500"><Trash2 className="w-4 h-4" /></button>
                </div>
              ))}
              {responsibles.length === 0 && <p className="text-xs text-muted-foreground text-center py-3">Aún no agregas responsables</p>}
            </div>
          </div>
        </TabsContent>

        <TabsContent value="miembros" className="mt-6 space-y-4">
          {invitations.length > 0 && (
            <div className="rounded-2xl border border-emerald-500/30 bg-emerald-500/5 p-5 max-w-lg" data-testid="invitations-inbox">
              <div className="flex items-center gap-2 mb-3"><Inbox className="w-5 h-5 text-emerald-500" /><h3 className="font-semibold">Invitaciones recibidas</h3></div>
              <div className="space-y-2">
                {invitations.map((inv) => (
                  <div key={inv.id} data-testid={`invitation-${inv.id}`} className="flex items-center gap-3 rounded-xl border border-border bg-card px-3 py-2.5">
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-medium truncate">{inv.vault_name}</p>
                      <p className="text-xs text-muted-foreground truncate">
                        {inv.invited_by_name ? `De ${inv.invited_by_name} · ` : ""}{ROLE_LABEL[inv.role] || inv.role}
                      </p>
                    </div>
                    <button data-testid={`accept-invite-${inv.id}`} onClick={() => acceptInvite(inv.id)} title="Aceptar"
                      className="w-8 h-8 rounded-lg bg-emerald-500/10 hover:bg-emerald-500/20 flex items-center justify-center text-emerald-500"><Check className="w-4 h-4" /></button>
                    <button data-testid={`decline-invite-${inv.id}`} onClick={() => declineInvite(inv.id)} title="Rechazar"
                      className="w-8 h-8 rounded-lg hover:bg-rose-500/10 flex items-center justify-center text-muted-foreground hover:text-rose-500"><X className="w-4 h-4" /></button>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="rounded-2xl border border-border bg-card p-5 max-w-lg">
            <div className="flex items-center gap-2 mb-4"><Users className="w-5 h-5 text-emerald-500" /><h3 className="font-semibold">{user?.vault_name}</h3></div>
            {isOwner && (
              <div className="flex flex-col sm:flex-row gap-2 mb-2">
                <Input data-testid="invite-email" type="email" value={invite} onChange={(e) => setInvite(e.target.value)} placeholder="correo@ejemplo.com" className="rounded-xl flex-1" onKeyDown={(e) => e.key === "Enter" && sendInvite()} />
                <Select value={inviteRole} onValueChange={setInviteRole}>
                  <SelectTrigger data-testid="invite-role" className="rounded-xl sm:w-40"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="member">Colaborador</SelectItem>
                    <SelectItem value="viewer">Solo lectura</SelectItem>
                  </SelectContent>
                </Select>
                <Button data-testid="invite-button" className="rounded-xl gap-2 shrink-0" onClick={sendInvite}><UserPlus className="w-4 h-4" /> Invitar</Button>
              </div>
            )}
            {isOwner && <p className="text-xs text-muted-foreground mb-4">El colaborador puede registrar y editar movimientos; solo lectura únicamente puede ver.</p>}
            <div className="space-y-2">
              {members.map((m) => (
                <div key={m.id} data-testid={`member-${m.id}`} className="flex items-center gap-3 rounded-xl border border-border px-3 py-2.5">
                  {m.picture ? <img src={m.picture} alt="" className="w-8 h-8 rounded-full" /> : <div className="w-8 h-8 rounded-full bg-emerald-500/15 flex items-center justify-center text-emerald-500 text-sm font-bold">{(m.name || m.email || "?")[0].toUpperCase()}</div>}
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-medium truncate flex items-center gap-1.5">
                      {m.name || m.email}
                      {m.role === "owner" && <Crown className="w-3.5 h-3.5 text-amber-500" />}
                      {m.role === "viewer" && <Eye className="w-3.5 h-3.5 text-sky-400" />}
                    </p>
                    <p className="text-xs text-muted-foreground truncate">
                      {m.status === "pending" ? "Invitación pendiente · " : ""}{ROLE_LABEL[m.role] || m.role}
                    </p>
                  </div>
                  {isOwner && m.role !== "owner" && (
                    <>
                      <Select value={m.role} onValueChange={(v) => changeRole(m.id, v)}>
                        <SelectTrigger data-testid={`role-select-${m.id}`} className="rounded-lg h-8 w-32 text-xs"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="member">Colaborador</SelectItem>
                          <SelectItem value="viewer">Solo lectura</SelectItem>
                        </SelectContent>
                      </Select>
                      <button data-testid={`remove-member-${m.id}`} onClick={() => removeMember(m.id)} className="text-muted-foreground hover:text-rose-500"><Trash2 className="w-4 h-4" /></button>
                    </>
                  )}
                </div>
              ))}
            </div>
            {!isOwner && (
              <div className="mt-4 pt-4 border-t border-border space-y-3">
                <p className="text-xs text-muted-foreground flex items-center gap-1.5"><Mail className="w-3.5 h-3.5" /> Solo el dueño puede invitar o quitar miembros.</p>
                <Button data-testid="leave-vault-button" variant="outline" onClick={leaveVault}
                  className="rounded-xl gap-2 w-full text-rose-500 border-rose-500/30 hover:bg-rose-500/10 hover:text-rose-500">
                  <DoorOpen className="w-4 h-4" /> Abandonar esta bóveda
                </Button>
              </div>
            )}
          </div>
        </TabsContent>

        <TabsContent value="moneda" className="mt-6">
          <MoneyTab />
        </TabsContent>

        <TabsContent value="notificaciones" className="mt-6">
          <div className="rounded-2xl border border-border bg-card p-6 max-w-lg">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-11 h-11 rounded-xl bg-emerald-500/10 flex items-center justify-center"><BellRing className="w-5 h-5 text-emerald-500" /></div>
              <div><h3 className="font-semibold">Recordatorios de próximos pagos</h3><p className="text-xs text-muted-foreground">Notificaciones push del navegador</p></div>
            </div>
            {!pushSupported() ? (
              <div className="space-y-3">
                <p className="text-sm text-muted-foreground">Tu navegador no soporta notificaciones push.</p>
                <Button data-testid="enable-push-button" className="rounded-xl gap-2" disabled><BellRing className="w-4 h-4" /> Activar recordatorios</Button>
              </div>
            ) : pushStatus === "denied" ? (
              <div className="space-y-3">
                <p className="text-sm text-rose-500">Bloqueaste las notificaciones. Actívalas desde los permisos del navegador y vuelve a intentar.</p>
                <Button data-testid="enable-push-button" className="rounded-xl gap-2" onClick={doEnablePush} disabled={pushBusy}><BellRing className="w-4 h-4" /> Reintentar</Button>
              </div>
            ) : pushStatus === "on" ? (
              <div className="space-y-3">
                <p className="text-sm text-emerald-500 flex items-center gap-2"><Bell className="w-4 h-4" /> Notificaciones activadas en este dispositivo.</p>
                <Button data-testid="test-push-button" variant="outline" className="rounded-xl gap-2" onClick={doTest}><BellRing className="w-4 h-4" /> Enviar notificación de prueba</Button>
              </div>
            ) : (
              <Button data-testid="enable-push-button" className="rounded-xl gap-2" onClick={doEnablePush} disabled={pushBusy}>
                {pushBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <BellRing className="w-4 h-4" />} Activar recordatorios
              </Button>
            )}
            <p className="text-xs text-muted-foreground mt-4">Te avisaremos cuando un pago recurrente esté por vencer (revisión diaria).</p>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
